// Right panel — redesigned: empty state, loading, full analysis results
import {
    Box, Typography, Paper, Chip, Grid,
    CircularProgress, LinearProgress, alpha, useTheme,
} from '@mui/material';
import {
    Analytics as AnalyticsIcon,
    Link as LinkIcon,
    Tag as TagIcon,
    TextFields as TextFieldsIcon,
    TrendingUp as TrendingUpIcon,
    AutoAwesome as AutoAwesomeIcon,
    SentimentSatisfiedAlt as SentimentPositiveIcon,
    SentimentDissatisfied as SentimentNegativeIcon,
    SentimentNeutral as SentimentNeutralIcon,
    CheckCircle as CheckIcon,
    Cancel as XIcon,
    Psychology as PsychologyIcon,
    Bolt as BoltIcon,
    Sell as HashtagIcon,
    RecordVoiceOver as CTAIcon,
    RecordVoiceOver as VoiceIcon,
    School as ReadingIcon,
    SyncAlt as SyncIcon,
    VerifiedUser as NicheIcon,
    EmojiObjects as HookIcon,
    Favorite as HeartIcon,
    BarChart as PercentileIcon,
    Fingerprint as FingerprintIcon,
    ArrowUpward as UpIcon,
    ArrowDownward as DownIcon,
    Speed as RetentionIcon,
    Search as DiscoverIcon,
    Forum as CommentIcon,
    BookmarkBorder as SaveTriggerIcon,
    Share as ShareTriggerIcon,
    ThumbUp as LikeIcon,
    Warning as WarningIcon,
    CheckCircle as CheckGreenIcon,
} from '@mui/icons-material';
import { MetricTile, ConfidenceBadge } from './AnalyzePrimitives';
import { FEATURE_CHIPS } from './constants';

const PLATFORM_META = {
    Twitter:   { color: '#1DA1F2', limit: '280 chars' },
    Instagram: { color: '#E1306C', limit: '2,200 chars' },
    LinkedIn:  { color: '#0A66C2', limit: '3,000 chars' },
    Reddit:    { color: '#FF4500', limit: '40,000 chars' },
    YouTube:   { color: '#FF0000', limit: '5,000 chars' },
};
const GRADE_META = {
    A: { color: '#10b981', label: 'Excellent' },
    B: { color: '#6366f1', label: 'Good' },
    C: { color: '#f59e0b', label: 'Fair' },
    D: { color: '#ef4444', label: 'Needs Work' },
    F: { color: '#dc2626', label: 'Poor' },
};
const TONE_META = {
    Professional: { color: '#0A66C2' },
    Educational:  { color: '#8b5cf6' },
    Urgent:       { color: '#ef4444' },
    Inspirational:{ color: '#f59e0b' },
    Casual:       { color: '#10b981' },
};
const _SIGNAL_DEFS = {
    YouTube: [
        { key: 'has_question', label: 'Hook/question in title',   tip: 'Question titles boost YouTube CTR by 2×' },
        { key: 'has_emojis',   label: 'Uses emojis',              tip: 'Emojis in description aid scannability' },
        { key: 'has_numbers',  label: 'Numbers in title/desc',    tip: 'Numbers boost click-through rate' },
        { key: 'has_hashtags', label: 'Has hashtags',             tip: 'YouTube hashtags improve search visibility' },
        { key: 'has_cta',      label: 'Has a call-to-action',     tip: 'Like/Subscribe CTAs retain viewers' },
        { key: 'has_url',      label: 'Includes a URL',           tip: 'Links in description drive traffic' },
    ],
    LinkedIn: [
        { key: 'has_question', label: 'Starts with a question',   tip: 'Questions get 2× more LinkedIn comments' },
        { key: 'has_emojis',   label: 'Uses emojis sparingly',    tip: 'Professional emoji use lifts engagement 15%' },
        { key: 'has_numbers',  label: 'Data and statistics',      tip: 'Stats boosted credibility on LinkedIn' },
        { key: 'has_hashtags', label: 'Professional hashtags',    tip: '3–5 hashtags is optimal for LinkedIn reach' },
        { key: 'has_cta',      label: 'Has a call-to-action',     tip: 'CTAs increase profile visits and saves' },
        { key: 'has_url',      label: 'Includes a URL',           tip: 'Links can reduce organic reach slightly' },
    ],
    Twitter: [
        { key: 'has_question', label: 'Includes a question',      tip: 'Questions drive replies and retweets' },
        { key: 'has_emojis',   label: 'Uses emojis',              tip: 'Emojis increase tweet engagement' },
        { key: 'has_numbers',  label: 'Contains stats/numbers',   tip: 'Numbers in tweets increase shares' },
        { key: 'has_hashtags', label: 'Uses 1–2 hashtags',        tip: '1–2 hashtags optimal on Twitter/X' },
        { key: 'has_cta',      label: 'Has a call-to-action',     tip: 'Explicit CTAs boost retweet rate' },
        { key: 'has_url',      label: 'Includes a URL',           tip: 'Links drive traffic but can reduce reach' },
    ],
    Instagram: [
        { key: 'has_question', label: 'Includes a question',      tip: 'Questions drive comments on Instagram' },
        { key: 'has_emojis',   label: 'Uses emojis',              tip: 'Emojis are standard in Instagram captions' },
        { key: 'has_numbers',  label: 'Contains stats/numbers',   tip: 'Numbers create instant curiosity' },
        { key: 'has_hashtags', label: 'Has hashtags',             tip: '5–15 hashtags maximize Instagram reach' },
        { key: 'has_cta',      label: 'Has a call-to-action',     tip: '"Save this post" CTAs boost saves' },
        { key: 'has_url',      label: 'Link mention',             tip: 'Direct caption links are ignored; use bio' },
    ],
    Reddit: [
        { key: 'has_question', label: 'Poses a question',         tip: 'Questions get more Reddit upvotes' },
        { key: 'has_emojis',   label: 'Minimal emoji use',        tip: 'Reddit culture prefers plain text over emojis' },
        { key: 'has_numbers',  label: 'Contains data/numbers',    tip: 'Data-backed posts score higher on Reddit' },
        { key: 'has_hashtags', label: 'Hashtags used',            tip: 'Hashtags are uncommon and ignored on Reddit' },
        { key: 'has_cta',      label: 'Has a call-to-action',     tip: 'Subtle CTAs work better; hard sells get downvoted' },
        { key: 'has_url',      label: 'Includes a URL',           tip: 'Link posts often get high engagement on Reddit' },
    ],
    default: [
        { key: 'has_question', label: 'Includes a question',      tip: 'Questions drive 2× more comments' },
        { key: 'has_emojis',   label: 'Uses emojis',              tip: 'Boosts engagement by up to 15%' },
        { key: 'has_numbers',  label: 'Contains stats/numbers',   tip: 'Numbers increase credibility' },
        { key: 'has_hashtags', label: 'Has hashtags',             tip: 'Improves discoverability' },
        { key: 'has_cta',      label: 'Has a call-to-action',     tip: 'CTAs drive user action' },
        { key: 'has_url',      label: 'Includes a URL',           tip: 'Links drive traffic' },
    ],
};
function getSignalDefs(platform) {
    return _SIGNAL_DEFS[platform] || _SIGNAL_DEFS.default;
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyState() {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    return (
        <Box sx={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', py: 8, px: 4,
            borderRadius: '12px',
            background: isDark
                ? `linear-gradient(135deg, ${alpha('#6366f1', 0.14)}, ${alpha('#8b5cf6', 0.10)})`
                : `linear-gradient(135deg, ${alpha('#6366f1', 0.04)}, ${alpha('#8b5cf6', 0.02)})`,
            border: `1.5px dashed ${alpha('#6366f1', isDark ? 0.45 : 0.25)}`,
        }}>
            <Box sx={{
                width: 72, height: 72, borderRadius: '16px', mx: 'auto', mb: 3,
                background: `linear-gradient(135deg, ${alpha('#6366f1', 0.15)}, ${alpha('#8b5cf6', 0.15)})`,
                border: `1px solid ${alpha('#6366f1', 0.2)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <AnalyticsIcon sx={{ fontSize: 36, color: '#6366f1' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1.5, letterSpacing: -0.3 }}>
                Unlock Content Insights
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 380, mx: 'auto', lineHeight: 1.7, mb: 4 }}>
                Paste your post on the left and click{' '}
                <Box component="span" sx={{ color: '#6366f1', fontWeight: 700 }}>Analyse Content</Box>{' '}
                to get AI-powered insights instantly.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, justifyContent: 'center' }}>
                {FEATURE_CHIPS.map(f => (
                    <Box key={f.label} sx={{
                        px: 1.5, py: 0.75, borderRadius: '8px',
                        bgcolor: alpha(f.color, isDark ? 0.12 : 0.08),
                        border: `1px solid ${alpha(f.color, 0.2)}`,
                    }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: f.color, fontSize: '0.75rem' }}>
                            {f.label}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Loading state ─────────────────────────────────────────────────────────────
function LoadingState() {
    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={56} thickness={3} sx={{ color: '#6366f1', mb: 3 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Running AI Analysis…</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Checking sentiment, readability, platform fitness and more
            </Typography>
            <LinearProgress sx={{ width: 260, borderRadius: 2, '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#6366f1,#8b5cf6)' } }} />
        </Box>
    );
}

// ── Card section wrapper ──────────────────────────────────────────────────────
function Section({ title, icon, color, badge, children, sx: sxProp }) {
    const theme = useTheme();
    const c = color || '#6366f1';
    return (
        <Paper elevation={0} sx={{
            p: 3, borderRadius: '12px',
            background: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            ...sxProp,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <Box sx={{
                    width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
                    bgcolor: alpha(c, 0.12), color: c,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {icon}
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>{title}</Typography>
                {badge}
            </Box>
            {children}
        </Paper>
    );
}

// ── Platform Radar chart (dynamic N-axis SVG) ────────────────────────────────
const _PLATFORM_COLORS = {
    Twitter: '#1DA1F2', Instagram: '#E1306C', LinkedIn: '#0A66C2',
    Reddit: '#FF4500',  YouTube: '#FF0000',
};
function PlatformRadar({ scores, platforms: platformsProp }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const PLATFORMS = platformsProp || ['Twitter', 'Instagram', 'LinkedIn', 'Reddit', 'YouTube'];
    const COLORS = PLATFORMS.map(p => _PLATFORM_COLORS[p] || '#6366f1');
    const SIZE = 220;
    const CX = SIZE / 2;
    const CY = SIZE / 2;
    const R = 80;
    const N = PLATFORMS.length;
    const textColor = isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)';
    const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)';

    const angle = (i) => (i * 2 * Math.PI / N) - Math.PI / 2;
    const pt = (i, r) => ({
        x: CX + r * Math.cos(angle(i)),
        y: CY + r * Math.sin(angle(i)),
    });

    const polyPoints = (r) => PLATFORMS.map((_, i) => {
        const p = pt(i, r);
        return `${p.x},${p.y}`;
    }).join(' ');

    const dataPoints = PLATFORMS.map((plat, i) => {
        const v = (scores[plat] || 0) / 100;
        const p = pt(i, R * v);
        return `${p.x},${p.y}`;
    }).join(' ');

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <svg width={SIZE} height={SIZE} style={{ overflow: 'visible' }}>
                {/* Grid rings */}
                {[0.25, 0.5, 0.75, 1.0].map(level => (
                    <polygon key={level} points={polyPoints(R * level)}
                        fill="none" stroke={gridColor} strokeWidth={1} />
                ))}
                {/* Axis lines */}
                {PLATFORMS.map((_, i) => {
                    const end = pt(i, R);
                    return <line key={i} x1={CX} y1={CY} x2={end.x} y2={end.y}
                        stroke={gridColor} strokeWidth={1.5} />;
                })}
                {/* Data fill */}
                <polygon points={dataPoints}
                    fill={alpha('#6366f1', isDark ? 0.2 : 0.12)}
                    stroke="#6366f1" strokeWidth={2} strokeLinejoin="round" />
                {/* Data dots */}
                {PLATFORMS.map((plat, i) => {
                    const v = (scores[plat] || 0) / 100;
                    const p = pt(i, R * v);
                    return (
                        <circle key={i} cx={p.x} cy={p.y} r={5}
                            fill={COLORS[i]} stroke={isDark ? '#1e1e2e' : 'white'} strokeWidth={2} />
                    );
                })}
                {/* Labels */}
                {PLATFORMS.map((plat, i) => {
                    const p = pt(i, R + 22);
                    const score = scores[plat] || 0;
                    return (
                        <g key={i}>
                            <text x={p.x} y={p.y - 2} textAnchor="middle"
                                fontSize="10" fontWeight="700" fill={COLORS[i]}>
                                {plat}
                            </text>
                            <text x={p.x} y={p.y + 12} textAnchor="middle"
                                fontSize="9" fill={textColor}>
                                {score}%
                            </text>
                        </g>
                    );
                })}
            </svg>
        </Box>
    );
}

// ── Score breakdown mini bar ──────────────────────────────────────────────────
function ScoreBreakdown({ engagement, readability, sentiment, sentimentLabel }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Match backend composite: positive=score*100, neutral=50, negative=(1-score)*100
    const sentimentValue = sentimentLabel === 'positive'
        ? Math.round(sentiment * 100)
        : sentimentLabel === 'negative'
        ? Math.round((1 - sentiment) * 100)
        : 50;

    const items = [
        { label: 'Engagement', value: Math.round(engagement * 100), weight: '40%', color: '#6366f1' },
        { label: 'Readability', value: Math.round(readability * 10), weight: '30%', color: '#8b5cf6' },
        { label: 'Sentiment', value: sentimentValue, weight: '30%', color: '#10b981' },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            {items.map(({ label, value, weight, color }) => (
                <Box key={label}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                        <Typography sx={{ color: 'text.secondary', fontSize: '0.82rem', fontWeight: 600 }}>
                            {label}{' '}
                            <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.75rem', fontWeight: 400 }}>
                                ({weight})
                            </Box>
                        </Typography>
                        <Typography sx={{ fontWeight: 800, color, fontSize: '0.9rem' }}>{value}%</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={Math.min(100, value)} sx={{
                        height: 7, borderRadius: '4px',
                        bgcolor: alpha(color, isDark ? 0.15 : 0.1),
                        '& .MuiLinearProgress-bar': { borderRadius: '4px', bgcolor: color },
                    }} />
                </Box>
            ))}
        </Box>
    );
}

// ── Niche Percentile Card ─────────────────────────────────────────────────────
function NichePercentileCard({ nichePercentile, nicheFit, card }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    if (!nichePercentile) return null;
    const { percentile, label, color, niche_display, niche_tip, thresholds, confidence } = nichePercentile;
    const fit = nicheFit;

    return (
        <Paper elevation={0} sx={{ ...card }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: alpha(color, 0.12), color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <PercentileIcon sx={{ fontSize: 17 }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Niche Benchmark</Typography>
                <ConfidenceBadge confidence={confidence} />
                <Box sx={{ px: 1.25, py: 0.3, borderRadius: '7px', bgcolor: alpha(color, 0.12), border: `1px solid ${alpha(color, 0.3)}` }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, color, fontSize: '0.72rem' }}>{label}</Typography>
                </Box>
            </Box>

            {/* Percentile bar */}
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.8rem', fontWeight: 600 }}>
                        Better than <Box component="span" sx={{ color, fontWeight: 800 }}>{percentile}%</Box> of {niche_display} posts
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        {[{ label: 'Median', val: thresholds?.median }, { label: 'Top 25%', val: thresholds?.top_25 }, { label: 'Top 10%', val: thresholds?.top_10 }].map(t => (
                            <Typography key={t.label} variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>{t.label}: {t.val}</Typography>
                        ))}
                    </Box>
                </Box>
                <Box sx={{ position: 'relative', height: 10, borderRadius: '5px', bgcolor: alpha(color, isDark ? 0.15 : 0.1) }}>
                    <Box sx={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${Math.min(100, percentile)}%`, borderRadius: '5px', bgcolor: color, transition: 'width 0.6s ease' }} />
                    {/* Threshold markers */}
                    {[thresholds?.median, thresholds?.top_25, thresholds?.top_10].filter(Boolean).map((t, i) => (
                        <Box key={i} sx={{ position: 'absolute', left: `${t}%`, top: -2, width: 2, height: 14, bgcolor: alpha('#fff', 0.5), borderRadius: 1 }} />
                    ))}
                </Box>
            </Box>

            {/* Niche fit gaps/strengths */}
            {fit && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                    {fit.strengths?.slice(0, 2).map((s, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                            <UpIcon sx={{ fontSize: 13, color: '#10b981', mt: '3px', flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.5 }}>{s}</Typography>
                        </Box>
                    ))}
                    {fit.gaps?.slice(0, 2).map((g, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                            <DownIcon sx={{ fontSize: 13, color: '#f59e0b', mt: '3px', flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.5 }}>{g}</Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Ideal formats for this niche */}
            {fit?.ideal_formats?.length > 0 && (
                <Box sx={{ mt: 1.25 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.disabled', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5, display: 'block' }}>
                        Best Formats for {niche_display}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {fit.ideal_formats.map((f, i) => (
                            <Box key={i} sx={{ px: 1, py: 0.3, borderRadius: '6px', bgcolor: alpha(color, isDark ? 0.08 : 0.05), border: `1px solid ${alpha(color, 0.2)}` }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color, fontSize: '0.7rem', textTransform: 'capitalize' }}>{f.replace('_', ' ')}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {niche_tip && (
                <Box sx={{ mt: 1.5, p: 1.25, borderRadius: '8px', bgcolor: alpha(color, isDark ? 0.07 : 0.04), border: `1px solid ${alpha(color, 0.15)}` }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem', fontStyle: 'italic' }}>💡 {niche_tip}</Typography>
                </Box>
            )}
        </Paper>
    );
}

// ── Narrative Structure Card ───────────────────────────────────────────────────
const _HOOK_COLORS = {
    stat: '#6366f1', story: '#10b981', bold_claim: '#ef4444',
    curiosity_gap: '#f59e0b', question: '#0ea5e9', how_to: '#8b5cf6',
    list: '#06b6d4', none: '#94a3b8',
};
const _EMOTION_COLORS = {
    curiosity: '#f59e0b', inspiration: '#10b981', fomo: '#ef4444',
    community: '#6366f1', authority: '#0ea5e9', humor: '#ec4899', empathy: '#8b5cf6',
};

function NarrativeCard({ narrative, card }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    if (!narrative) return null;
    const { hook_type, emotional_triggers, arc, narrative_score, content_format, payoff, confidence } = narrative;
    const hookColor = _HOOK_COLORS[hook_type?.type] || '#6366f1';
    const arcColor = arc?.arc_score >= 70 ? '#10b981' : arc?.arc_score >= 45 ? '#f59e0b' : '#ef4444';

    return (
        <Paper elevation={0} sx={{ ...card }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: alpha('#ec4899', 0.12), color: '#ec4899', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <HookIcon sx={{ fontSize: 17 }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Narrative Structure</Typography>
                <ConfidenceBadge confidence={confidence} />
                <Box sx={{ px: 1.25, py: 0.3, borderRadius: '7px', bgcolor: alpha('#ec4899', 0.1), border: `1px solid ${alpha('#ec4899', 0.25)}` }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#ec4899', fontSize: '0.72rem' }}>Score: {narrative_score}</Typography>
                </Box>
            </Box>

            {/* Hook type */}
            <Box sx={{ mb: 1.75, p: 1.25, borderRadius: '9px', bgcolor: alpha(hookColor, isDark ? 0.1 : 0.05), border: `1px solid ${alpha(hookColor, 0.2)}`, borderLeft: `3px solid ${hookColor}` }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: hookColor, fontSize: '0.7rem', display: 'block', mb: 0.3 }}>
                    Hook Type: {hook_type?.label}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.5 }}>
                    {hook_type?.description}
                </Typography>
            </Box>

            {/* Content format + Payoff */}
            {(content_format || payoff) && (
                <Box sx={{ display: 'flex', gap: 1, mb: 1.75, flexWrap: 'wrap' }}>
                    {content_format && content_format.format !== 'general' && (
                        <Box sx={{ px: 1.25, py: 0.5, borderRadius: '8px', bgcolor: alpha(content_format.color || '#6366f1', isDark ? 0.1 : 0.06), border: `1px solid ${alpha(content_format.color || '#6366f1', 0.25)}`, flex: 1, minWidth: 120 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: content_format.color || '#6366f1', fontSize: '0.68rem', display: 'block', mb: 0.2 }}>
                                Format: {content_format.label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem', lineHeight: 1.3 }}>{content_format.description}</Typography>
                        </Box>
                    )}
                    {payoff && payoff.delivered !== null && (
                        <Box sx={{ px: 1.25, py: 0.5, borderRadius: '8px', bgcolor: alpha(payoff.delivered ? '#10b981' : '#f59e0b', isDark ? 0.1 : 0.06), border: `1px solid ${alpha(payoff.delivered ? '#10b981' : '#f59e0b', 0.25)}`, flex: 1, minWidth: 120 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: payoff.delivered ? '#10b981' : '#f59e0b', fontSize: '0.68rem', display: 'block', mb: 0.2 }}>
                                Payoff: {payoff.delivered ? 'Delivered' : 'Incomplete'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem', lineHeight: 1.3 }}>{payoff.reason}</Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* Arc scores */}
            {arc && (
                <Box sx={{ mb: 1.75 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.disabled', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
                        Narrative Arc
                    </Typography>
                    {[
                        { label: 'Hook', score: arc.hook_score, tip: 'Opening attention signal' },
                        { label: 'Body', score: arc.body_score, tip: 'Development & reasoning' },
                        { label: 'CTA', score: arc.cta_score, tip: arc.cta_placement === 'missing' ? 'No CTA detected' : `CTA in ${arc.cta_placement}` },
                    ].map(({ label, score, tip }) => {
                        const c = score >= 70 ? '#10b981' : score >= 45 ? '#f59e0b' : '#ef4444';
                        return (
                            <Box key={label} sx={{ mb: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.78rem' }}>{label}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>{tip}</Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: c, fontSize: '0.78rem' }}>{score}</Typography>
                                    </Box>
                                </Box>
                                <LinearProgress variant="determinate" value={score} sx={{ height: 5, borderRadius: '3px', bgcolor: alpha(c, isDark ? 0.15 : 0.1), '& .MuiLinearProgress-bar': { borderRadius: '3px', bgcolor: c } }} />
                            </Box>
                        );
                    })}
                </Box>
            )}

            {/* Emotional triggers */}
            {emotional_triggers?.length > 0 && (
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.disabled', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75, display: 'block' }}>
                        Emotional Triggers ({emotional_triggers.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                        {emotional_triggers.slice(0, 5).map((t, i) => {
                            const ec = _EMOTION_COLORS[t.type] || '#6366f1';
                            return (
                                <Box key={i} sx={{ px: 1.25, py: 0.4, borderRadius: '7px', bgcolor: alpha(ec, isDark ? 0.12 : 0.07), border: `1px solid ${alpha(ec, 0.25)}` }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: ec, fontSize: '0.72rem' }}>{t.label}</Typography>
                                </Box>
                            );
                        })}
                        {emotional_triggers.length === 0 && (
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.78rem' }}>No emotional triggers detected — add curiosity or inspiration language</Typography>
                        )}
                    </Box>
                </Box>
            )}

            {/* Arc recommendations */}
            {arc?.recommendations?.length > 0 && (
                <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {arc.recommendations.map((r, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: arcColor, mt: '6px', flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.5 }}>{r}</Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Paper>
    );
}

// ── Voice Consistency Card ────────────────────────────────────────────────────
function VoiceConsistencyCard({ voiceConsistency, card }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    if (!voiceConsistency) return null;
    if (!voiceConsistency.available) {
        return (
            <Paper elevation={0} sx={{ ...card }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: alpha('#8b5cf6', 0.12), color: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FingerprintIcon sx={{ fontSize: 17 }} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Voice Consistency</Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.8rem' }}>{voiceConsistency.reason}</Typography>
            </Paper>
        );
    }

    const { consistency_score, drifts, matches, style_summary, confidence } = voiceConsistency;
    const scoreColor = consistency_score >= 75 ? '#10b981' : consistency_score >= 50 ? '#f59e0b' : '#ef4444';
    const scoreLabel = consistency_score >= 75 ? 'On-Brand' : consistency_score >= 50 ? 'Slight Drift' : 'Voice Drift';

    return (
        <Paper elevation={0} sx={{ ...card }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: alpha('#8b5cf6', 0.12), color: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FingerprintIcon sx={{ fontSize: 17 }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Voice Consistency</Typography>
                <ConfidenceBadge confidence={confidence} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="h6" sx={{ fontWeight: 900, color: scoreColor, lineHeight: 1 }}>{consistency_score}</Typography>
                    <Box sx={{ px: 1, py: 0.25, borderRadius: '6px', bgcolor: alpha(scoreColor, 0.12), border: `1px solid ${alpha(scoreColor, 0.3)}` }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: scoreColor, fontSize: '0.68rem' }}>{scoreLabel}</Typography>
                    </Box>
                </Box>
            </Box>

            {style_summary && (
                <Box sx={{ mb: 1.5, p: 1, borderRadius: '8px', bgcolor: alpha('#8b5cf6', isDark ? 0.07 : 0.04), border: `1px solid ${alpha('#8b5cf6', 0.15)}` }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem', fontStyle: 'italic' }}>{style_summary}</Typography>
                </Box>
            )}

            {drifts?.length > 0 && (
                <Box sx={{ mb: 1.25 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#f59e0b', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5, display: 'block' }}>Voice Drifts</Typography>
                    {drifts.map((d, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.5 }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: d.severity === 'high' ? '#ef4444' : '#f59e0b', mt: '6px', flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.5 }}>
                                <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{d.label}: </Box>
                                {d.detail}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {matches?.length > 0 && (
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#10b981', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5, display: 'block' }}>On-Brand</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {matches.slice(0, 4).map((m, i) => (
                            <Box key={i} sx={{ px: 1, py: 0.3, borderRadius: '6px', bgcolor: alpha('#10b981', isDark ? 0.1 : 0.06), border: `1px solid ${alpha('#10b981', 0.2)}` }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981', fontSize: '0.7rem' }}>{m.label}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Paper>
    );
}

// ── Retention Predictor Card ──────────────────────────────────────────────────
function RetentionCard({ retention, card }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    if (!retention?.available) return null;
    const { retention_score, opening_score, body_score, closing_score, drop_off_risks, retention_boosts, readability_shift, confidence } = retention;
    const scoreColor = retention_score >= 70 ? '#10b981' : retention_score >= 45 ? '#f59e0b' : '#ef4444';
    const scoreLabel = retention_score >= 70 ? 'Strong Retention' : retention_score >= 45 ? 'Moderate' : 'Drop-off Risk';

    const sections = [
        { label: 'Opening', score: opening_score, tip: 'Hook — first 20%' },
        { label: 'Body', score: body_score, tip: 'Pacing + structure' },
        { label: 'Closing', score: closing_score, tip: 'CTA + payoff' },
    ];

    return (
        <Paper elevation={0} sx={{ ...card }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: alpha(scoreColor, 0.12), color: scoreColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <RetentionIcon sx={{ fontSize: 17 }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Retention Predictor</Typography>
                <ConfidenceBadge confidence={confidence} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="h6" sx={{ fontWeight: 900, color: scoreColor, lineHeight: 1 }}>{retention_score}</Typography>
                    <Box sx={{ px: 1, py: 0.25, borderRadius: '6px', bgcolor: alpha(scoreColor, 0.12), border: `1px solid ${alpha(scoreColor, 0.3)}` }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: scoreColor, fontSize: '0.68rem' }}>{scoreLabel}</Typography>
                    </Box>
                </Box>
            </Box>

            {/* Section arc bars */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9, mb: 1.75 }}>
                {sections.map(({ label, score, tip }) => {
                    const c = score >= 70 ? '#10b981' : score >= 45 ? '#f59e0b' : '#ef4444';
                    return (
                        <Box key={label}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.78rem' }}>{label}</Typography>
                                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>{tip}</Typography>
                                </Box>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: c, fontSize: '0.78rem' }}>{score}</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={score} sx={{ height: 5, borderRadius: '3px', bgcolor: alpha(c, isDark ? 0.15 : 0.08), '& .MuiLinearProgress-bar': { borderRadius: '3px', bgcolor: c } }} />
                        </Box>
                    );
                })}
            </Box>

            {/* Readability shift warning */}
            {readability_shift?.detected && (
                <Box sx={{ mb: 1.5, p: 1.25, borderRadius: '8px', bgcolor: alpha('#f59e0b', isDark ? 0.08 : 0.04), border: `1px solid ${alpha('#f59e0b', 0.2)}`, borderLeft: '3px solid #f59e0b' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#f59e0b', fontSize: '0.65rem', textTransform: 'uppercase', display: 'block', mb: 0.25 }}>
                        Readability Drop
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.5 }}>
                        Sentences jump from avg {readability_shift.opening_avg_words} words (opening) to {readability_shift.body_avg_words} words (body) — {readability_shift.shift_pct}% denser. Readers who liked the punchy opener may bounce.
                    </Typography>
                </Box>
            )}

            {/* Drop-off risks */}
            {drop_off_risks?.length > 0 && (
                <Box sx={{ mb: 1.25 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#ef4444', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5, display: 'block' }}>
                        Drop-off Risks
                    </Typography>
                    {drop_off_risks.map((r, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.6 }}>
                            <WarningIcon sx={{ fontSize: 12, color: r.severity === 'high' ? '#ef4444' : '#f59e0b', mt: '3px', flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.5 }}>{r.description}</Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Retention boosts */}
            {retention_boosts?.length > 0 && (
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#10b981', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5, display: 'block' }}>
                        What's Working
                    </Typography>
                    {retention_boosts.slice(0, 3).map((b, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.4 }}>
                            <CheckGreenIcon sx={{ fontSize: 12, color: '#10b981', mt: '3px', flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.5 }}>{b}</Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Paper>
    );
}

// ── Discoverability Card ──────────────────────────────────────────────────────
function DiscoverabilityCard({ discoverability, card }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    if (!discoverability?.available) return null;
    const {
        seo_score, search_intent, intent_reach, platform_checklist,
        checklist_summary, discovery_gaps, top_priority_fix, confidence,
    } = discoverability;
    const scoreColor = seo_score >= 70 ? '#10b981' : seo_score >= 45 ? '#f59e0b' : '#ef4444';
    const passed = checklist_summary?.passed ?? 0;
    const total = checklist_summary?.total ?? 0;

    return (
        <Paper elevation={0} sx={{ ...card }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: alpha('#0ea5e9', 0.12), color: '#0ea5e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <DiscoverIcon sx={{ fontSize: 17 }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Discoverability</Typography>
                <ConfidenceBadge confidence={confidence} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="h6" sx={{ fontWeight: 900, color: scoreColor, lineHeight: 1 }}>{seo_score}</Typography>
                    <Box sx={{ px: 1, py: 0.25, borderRadius: '6px', bgcolor: alpha(scoreColor, 0.12), border: `1px solid ${alpha(scoreColor, 0.3)}` }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: scoreColor, fontSize: '0.68rem' }}>SEO Score</Typography>
                    </Box>
                </Box>
            </Box>

            {/* Intent + checklist summary */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1.75 }}>
                {search_intent && (
                    <Box sx={{ flex: 1, p: 1.25, borderRadius: '8px', bgcolor: alpha('#0ea5e9', isDark ? 0.08 : 0.04), border: `1px solid ${alpha('#0ea5e9', 0.15)}` }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#0ea5e9', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', mb: 0.3 }}>
                            Search Intent
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.78rem', display: 'block' }}>
                            {search_intent.charAt(0).toUpperCase() + search_intent.slice(1)}
                        </Typography>
                        {intent_reach && (
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem', lineHeight: 1.4 }}>{intent_reach}</Typography>
                        )}
                    </Box>
                )}
                {total > 0 && (
                    <Box sx={{ p: 1.25, borderRadius: '8px', bgcolor: alpha(passed === total ? '#10b981' : '#f59e0b', isDark ? 0.08 : 0.04), border: `1px solid ${alpha(passed === total ? '#10b981' : '#f59e0b', 0.15)}`, textAlign: 'center', minWidth: 72 }}>
                        <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color: passed === total ? '#10b981' : '#f59e0b', lineHeight: 1 }}>
                            {passed}/{total}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>checklist</Typography>
                    </Box>
                )}
            </Box>

            {/* Top priority fix */}
            {top_priority_fix && (
                <Box sx={{ mb: 1.5, p: 1.25, borderRadius: '8px', bgcolor: alpha('#ef4444', isDark ? 0.08 : 0.04), border: `1px solid ${alpha('#ef4444', 0.2)}`, borderLeft: '3px solid #ef4444' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#ef4444', fontSize: '0.65rem', textTransform: 'uppercase', display: 'block', mb: 0.25 }}>
                        #1 Priority Fix
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.5 }}>{top_priority_fix}</Typography>
                </Box>
            )}

            {/* Platform checklist */}
            {platform_checklist?.length > 0 && (
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.disabled', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.75, display: 'block' }}>
                        Platform Checklist
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                        {platform_checklist.map((item, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                {item.passed
                                    ? <CheckGreenIcon sx={{ fontSize: 13, color: '#10b981', mt: '2px', flexShrink: 0 }} />
                                    : <WarningIcon sx={{ fontSize: 13, color: item.impact === 'high' ? '#ef4444' : '#f59e0b', mt: '2px', flexShrink: 0 }} />
                                }
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="caption" sx={{ fontWeight: item.passed ? 600 : 700, color: item.passed ? 'text.secondary' : 'text.primary', fontSize: '0.78rem' }}>
                                        {item.item}
                                    </Typography>
                                    {!item.passed && (
                                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem', display: 'block', lineHeight: 1.4 }}>
                                            {item.tip}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Discovery gaps if no checklist */}
            {!platform_checklist?.length && discovery_gaps?.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {discovery_gaps.map((g, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                            <DownIcon sx={{ fontSize: 13, color: '#f59e0b', mt: '3px', flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.5 }}>{g}</Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Paper>
    );
}

// ── Engagement Breakdown Card ─────────────────────────────────────────────────
function EngagementBreakdownCard({ engagementBreakdown, card }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    if (!engagementBreakdown) return null;
    const { comment, save, share, like, dominant_action, optimization_tip } = engagementBreakdown;

    const metrics = [
        { key: 'comment', label: 'Comments', value: comment, icon: <CommentIcon sx={{ fontSize: 13 }} />, color: '#6366f1', tip: 'Questions + debate prompts' },
        { key: 'save',    label: 'Saves',    value: save,    icon: <SaveTriggerIcon sx={{ fontSize: 13 }} />, color: '#f59e0b', tip: 'Lists + educational content' },
        { key: 'share',   label: 'Shares',   value: share,   icon: <ShareTriggerIcon sx={{ fontSize: 13 }} />, color: '#ec4899', tip: 'Inspiration + surprise' },
        { key: 'like',    label: 'Likes',    value: like,    icon: <LikeIcon sx={{ fontSize: 13 }} />, color: '#10b981', tip: 'Relatable + emotional' },
    ];

    return (
        <Paper elevation={0} sx={{ ...card }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: alpha('#6366f1', 0.12), color: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <HeartIcon sx={{ fontSize: 17 }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Engagement Breakdown</Typography>
                {dominant_action && (
                    <Box sx={{ px: 1, py: 0.25, borderRadius: '6px', bgcolor: alpha('#6366f1', 0.1), border: `1px solid ${alpha('#6366f1', 0.25)}` }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#6366f1', fontSize: '0.68rem' }}>
                            Drives {dominant_action}s
                        </Typography>
                    </Box>
                )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1, mb: 1.5 }}>
                {metrics.map(({ key, label, value, icon, color, tip }) => {
                    const isDominant = key === dominant_action;
                    return (
                        <Box key={key}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4, alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                    <Box sx={{ color, display: 'flex' }}>{icon}</Box>
                                    <Typography variant="caption" sx={{ fontWeight: isDominant ? 800 : 600, color: isDominant ? color : 'text.secondary', fontSize: '0.78rem' }}>
                                        {label}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>{tip}</Typography>
                                </Box>
                                <Typography variant="caption" sx={{ fontWeight: 800, color, fontSize: '0.78rem' }}>{value}%</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={value} sx={{
                                height: isDominant ? 7 : 5, borderRadius: '3px',
                                bgcolor: alpha(color, isDark ? 0.15 : 0.08),
                                '& .MuiLinearProgress-bar': { borderRadius: '3px', bgcolor: color },
                            }} />
                        </Box>
                    );
                })}
            </Box>

            {optimization_tip && (
                <Box sx={{ p: 1.25, borderRadius: '8px', bgcolor: alpha('#6366f1', isDark ? 0.08 : 0.04), border: `1px solid ${alpha('#6366f1', 0.15)}` }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#6366f1', fontSize: '0.65rem', textTransform: 'uppercase', display: 'block', mb: 0.25 }}>
                        Boost Weakest Metric
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.5 }}>{optimization_tip}</Typography>
                </Box>
            )}
        </Paper>
    );
}

// ── Diagnostic Insights Card — cross-engine signal fusion ────────────────────
// Surfaces insights that only appear when correlating signals ACROSS engines.
// e.g. high SEO + low retention, cliffhanger hook + weak closing, etc.
function DiagnosticInsightsCard({ diagnoses, card }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    if (!diagnoses || !Array.isArray(diagnoses.diagnoses) || diagnoses.diagnoses.length === 0) return null;

    const SEV_CONFIG = {
        high:   { color: '#ef4444', label: 'High' },
        medium: { color: '#f59e0b', label: 'Medium' },
        low:    { color: '#6366f1', label: 'Low' },
    };

    return (
        <Paper elevation={0} sx={{ ...card }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{
                    width: 30, height: 30, borderRadius: '8px',
                    bgcolor: alpha('#f59e0b', 0.12), color: '#f59e0b',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <WarningIcon sx={{ fontSize: 17 }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Diagnostic Insights</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {diagnoses.severity_breakdown?.high > 0 && (
                        <Chip label={`${diagnoses.severity_breakdown.high} high`} size="small"
                            sx={{ bgcolor: alpha('#ef4444', 0.1), color: '#ef4444', fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                    )}
                    {diagnoses.severity_breakdown?.medium > 0 && (
                        <Chip label={`${diagnoses.severity_breakdown.medium} medium`} size="small"
                            sx={{ bgcolor: alpha('#f59e0b', 0.1), color: '#f59e0b', fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                    )}
                    <ConfidenceBadge confidence={diagnoses.confidence} />
                </Box>
            </Box>

            {diagnoses.diagnoses.map((d, i) => {
                const sev = SEV_CONFIG[d.severity] || SEV_CONFIG.medium;
                return (
                    <Box key={d.id || i} sx={{
                        display: 'flex', alignItems: 'flex-start', gap: 1.25,
                        p: 1.25, mb: i < diagnoses.diagnoses.length - 1 ? 0.75 : 0,
                        borderRadius: '8px',
                        bgcolor: alpha(sev.color, isDark ? 0.08 : 0.04),
                        border: `1px solid ${alpha(sev.color, 0.2)}`,
                        borderLeft: `3px solid ${sev.color}`,
                    }}>
                        <WarningIcon sx={{ fontSize: 14, color: sev.color, mt: '3px', flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.25, flexWrap: 'wrap' }}>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.78rem' }}>
                                    {d.title}
                                </Typography>
                                <Chip label={sev.label} size="small"
                                    sx={{ bgcolor: alpha(sev.color, 0.1), color: sev.color, fontWeight: 700, fontSize: '0.6rem', height: 18 }} />
                            </Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', lineHeight: 1.45, display: 'block' }}>
                                {d.detail}
                            </Typography>
                            {d.engines_involved?.length > 0 && (
                                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.63rem', display: 'block', mt: 0.25 }}>
                                    Detected via: {d.engines_involved.join(' + ')}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Paper>
    );
}

// ── Engine Correlation Card — outcome feedback loop (Phase 4) ────────────────
// Shows which engine scores actually correlate with real engagement for THIS
// creator, based on analysing their published post history vs actual metrics.
// Only visible once the user has ≥ 10 scored outcomes in Firestore.
function EngineCorrelationCard({ engineCorrelations, card }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    if (!engineCorrelations || !engineCorrelations.available) return null;
    const { insights = [], n_posts = 0 } = engineCorrelations;
    if (!insights || insights.length === 0) return null;

    const _directionColor = (dir) => dir === 'positive' ? '#10b981' : '#f59e0b';
    const _strengthBg = (strength) => {
        if (strength === 'strong') return isDark ? 'rgba(16,185,129,0.10)' : 'rgba(16,185,129,0.06)';
        if (strength === 'moderate') return isDark ? 'rgba(99,102,241,0.10)' : 'rgba(99,102,241,0.06)';
        return isDark ? 'rgba(148,163,184,0.08)' : 'rgba(148,163,184,0.05)';
    };
    const _strengthBorder = (strength) => {
        if (strength === 'strong') return 'rgba(16,185,129,0.25)';
        if (strength === 'moderate') return 'rgba(99,102,241,0.25)';
        return 'rgba(148,163,184,0.2)';
    };

    return (
        <Paper sx={card} elevation={0}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10b981', flexShrink: 0 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.82rem' }}>
                    What Actually Works For You
                </Typography>
                <Box sx={{ ml: 'auto', px: 1, py: 0.3, borderRadius: '6px', bgcolor: isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.25)' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#10b981', fontSize: '0.65rem' }}>
                        {n_posts} posts analysed
                    </Typography>
                </Box>
            </Box>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.71rem', lineHeight: 1.4, display: 'block', mb: 1.5 }}>
                Pearson correlation between your engine scores and actual engagement — data-backed, not generic advice.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {insights.map((ins, i) => (
                    <Box key={i} sx={{ p: 1.25, borderRadius: '8px', bgcolor: _strengthBg(ins.strength), border: `1px solid ${_strengthBorder(ins.strength)}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.4 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: _directionColor(ins.direction), fontSize: '0.72rem' }}>
                                {ins.label}
                            </Typography>
                            <Box sx={{ px: 0.7, py: 0.15, borderRadius: '4px', bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }}>
                                <Typography variant="caption" sx={{ fontSize: '0.63rem', color: 'text.secondary', fontFamily: 'monospace' }}>
                                    r={ins.r > 0 ? '+' : ''}{ins.r?.toFixed(2)} · {ins.strength}
                                </Typography>
                            </Box>
                            <Box sx={{ ml: 'auto' }}>
                                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.63rem' }}>
                                    {ins.n} posts
                                </Typography>
                            </Box>
                        </Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.69rem', lineHeight: 1.4 }}>
                            {ins.insight_text}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Paper>
    );
}

// ── Winning Patterns Card — personal signal-lift calibration ─────────────────
// Surfaces what actually works for THIS creator, based on their own post history.
// Shows positive/negative signal lifts with sample sizes and confidence.
function WinningPatternsCard({ signalLifts, card }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    if (!signalLifts) return null;
    if (!signalLifts.available) {
        // Empty / locked state — still render so user understands what unlocks
        return (
            <Paper elevation={0} sx={{ ...card }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: alpha('#6366f1', 0.12), color: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <TrendingUpIcon sx={{ fontSize: 17 }} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Your Winning Patterns</Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.8rem' }}>
                    {signalLifts.reason || 'Sync posts to unlock personal signal calibration'}
                </Typography>
            </Paper>
        );
    }

    const { winning_patterns = [], losing_patterns = [], sample_size, confidence } = signalLifts;
    if (winning_patterns.length === 0 && losing_patterns.length === 0) {
        return (
            <Paper elevation={0} sx={{ ...card }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: alpha('#6366f1', 0.12), color: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <TrendingUpIcon sx={{ fontSize: 17 }} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Your Winning Patterns</Typography>
                    <ConfidenceBadge confidence={confidence} />
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                    No signal has a clear effect on engagement yet across your {sample_size} posts — try more varied content to see patterns emerge.
                </Typography>
            </Paper>
        );
    }

    const renderLiftRow = (lift, variant) => {
        const color = variant === 'positive' ? '#10b981' : '#ef4444';
        const Icon = variant === 'positive' ? UpIcon : DownIcon;
        return (
            <Box key={lift.signal} sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.25, mb: 0.75,
                borderRadius: '8px',
                bgcolor: alpha(color, isDark ? 0.08 : 0.04),
                border: `1px solid ${alpha(color, 0.2)}`,
                borderLeft: `3px solid ${color}`,
            }}>
                <Icon sx={{ fontSize: 14, color, mt: '3px', flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.25, flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.78rem' }}>
                            {lift.label}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color, fontSize: '0.78rem' }}>
                                {lift.lift_pct > 0 ? '+' : ''}{lift.lift_pct}%
                            </Typography>
                            <ConfidenceBadge confidence={lift.confidence} />
                        </Box>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', lineHeight: 1.45, display: 'block' }}>
                        {lift.headline}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.66rem', display: 'block', mt: 0.25 }}>
                        {lift.n_with} posts with • {lift.n_without} without
                    </Typography>
                </Box>
            </Box>
        );
    };

    return (
        <Paper elevation={0} sx={{ ...card }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: alpha('#6366f1', 0.12), color: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <TrendingUpIcon sx={{ fontSize: 17 }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Your Winning Patterns</Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                    {sample_size} posts analyzed
                </Typography>
                <ConfidenceBadge confidence={confidence} />
            </Box>

            {winning_patterns.length > 0 && (
                <Box sx={{ mb: losing_patterns.length > 0 ? 1.25 : 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#10b981', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5, display: 'block' }}>
                        What Works For You
                    </Typography>
                    {winning_patterns.map(l => renderLiftRow(l, 'positive'))}
                </Box>
            )}

            {losing_patterns.length > 0 && (
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#ef4444', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5, display: 'block' }}>
                        What Hurts Your Engagement
                    </Typography>
                    {losing_patterns.map(l => renderLiftRow(l, 'negative'))}
                </Box>
            )}
        </Paper>
    );
}

// ── Full results ──────────────────────────────────────────────────────────────
function AnalysisResults({ analysis }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const sentimentColor =
        analysis.sentiment === 'positive' ? '#10b981' :
        analysis.sentiment === 'negative' ? '#ef4444' : '#f59e0b';

    const grade = analysis.overall_grade || 'C';
    const gradeMeta = GRADE_META[grade] || GRADE_META.C;
    const tone = analysis.tone || 'Casual';
    const toneMeta = TONE_META[tone] || { color: '#6366f1' };
    const virality = analysis.virality || 'Low';
    const viralColor = virality === 'High' ? '#10b981' : virality === 'Medium' ? '#f59e0b' : '#ef4444';
    const signals = analysis.content_signals || {};
    const platformScores = analysis.platform_scores || {};
    const ctaStrength = analysis.cta_strength || null;
    const ctaColor = ctaStrength === 'Strong' ? '#10b981' : ctaStrength === 'Moderate' ? '#f59e0b' : '#ef4444';
    const hashtagSuggestions = analysis.hashtag_suggestions || [];
    const readingLevel = analysis.reading_level || null;
    const hookAnalysis = analysis.hook_analysis || null;
    const readabilityContext = analysis.readability_context || null;
    const hasMissingKeywords = analysis.missing_keywords?.length > 0;
    const hookColors = { weak: '#ef4444', moderate: '#f59e0b', strong: '#10b981' };

    const narrative          = analysis.narrative || null;
    const nichePercentile    = analysis.niche_percentile || null;
    const nicheFit           = analysis.niche_fit || null;
    const voiceConsistency   = analysis.voice_consistency || null;
    const retention          = analysis.retention || null;
    const discoverability    = analysis.discoverability || null;
    const engagementBreakdown = analysis.engagement_breakdown || null;
    const signalLifts          = analysis.signal_lifts || null;
    const diagnoses            = analysis.diagnoses || null;
    const engineCorrelations   = analysis.engine_correlations || null;

    const cardSx = {
        p: 3, borderRadius: '12px',
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        elevation: 0,
    };

    const hasPlatformFitness = Object.keys(platformScores).length > 0;
    const hasSignals         = Object.keys(signals).length > 0;
    const hasKeywords        = analysis.keywords?.length > 0;
    const hasEntities        = analysis.entities?.length > 0;
    const hasHashtags        = hashtagSuggestions.length > 0;
    const hasSuggestions     = analysis.suggestions?.length > 0;

    // Show ALL platform scores; sort detected platform first, then by score desc
    const bestPlatform = analysis.best_platform;
    const sortedPlatformEntries = Object.entries(platformScores).sort(([aKey, aVal], [bKey, bVal]) => {
        if (aKey === bestPlatform) return -1;
        if (bKey === bestPlatform) return 1;
        return bVal - aVal;
    });
    const signalDefs = getSignalDefs(bestPlatform);

    return (
        <Grid container spacing={2.5}>

            {/* URL source banner — full width */}
            {analysis.original_url && (
                <Grid size={12}>
                    <Box sx={{
                        display: 'flex', alignItems: 'flex-start', gap: 1.25,
                        p: 1.75, borderRadius: '10px',
                        bgcolor: analysis.url_fetch_failed
                            ? alpha('#ef4444', isDark ? 0.1 : 0.06)
                            : alpha('#6366f1', isDark ? 0.1 : 0.06),
                        border: `1px solid ${alpha(analysis.url_fetch_failed ? '#ef4444' : '#6366f1', 0.25)}`,
                        borderLeft: `3px solid ${analysis.url_fetch_failed ? '#ef4444' : '#6366f1'}`,
                    }}>
                        <LinkIcon sx={{ fontSize: 16, color: analysis.url_fetch_failed ? '#ef4444' : '#6366f1', mt: 0.1, flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            {analysis.url_fetch_failed ? (
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#ef4444', display: 'block' }}>
                                    Could not fetch URL content — analysed the URL string itself
                                </Typography>
                            ) : (
                                <>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#6366f1', display: 'block' }}>
                                        Analysed from URL{analysis.fetched_title ? `: ${analysis.fetched_title}` : ''}
                                    </Typography>
                                    <Typography variant="caption" sx={{
                                        color: 'text.disabled', fontSize: '0.68rem', display: 'block',
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>
                                        {analysis.original_url}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Box>
                </Grid>
            )}

            {/* Cross-engine diagnostics: issues that span multiple engines */}
            {diagnoses && diagnoses.diagnoses?.length > 0 && (
                <Grid size={12}>
                    <DiagnosticInsightsCard diagnoses={diagnoses} card={cardSx} />
                </Grid>
            )}

            {/* Engine Correlation Card — what actually predicts YOUR engagement */}
            {engineCorrelations?.available && engineCorrelations.insights?.length > 0 && (
                <Grid size={12}>
                    <EngineCorrelationCard engineCorrelations={engineCorrelations} card={cardSx} />
                </Grid>
            )}

            {/* Top-priority personalization: Your Winning Patterns */}
            {signalLifts && (
                <Grid size={12}>
                    <WinningPatternsCard signalLifts={signalLifts} card={cardSx} />
                </Grid>
            )}

            {/* A: Score Header + Breakdown — full width */}
            <Grid size={12}>
            <Paper elevation={0} sx={{
                p: 3, borderRadius: '12px',
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
            }}>
                <Grid container spacing={3} alignItems="center">
                    {/* Grade + badges */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                            <Box sx={{
                                width: 72, height: 72, borderRadius: '12px', flexShrink: 0,
                                background: `linear-gradient(135deg, ${alpha(gradeMeta.color, 0.15)}, ${alpha(gradeMeta.color, 0.07)})`,
                                border: `2px solid ${alpha(gradeMeta.color, 0.3)}`,
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <Typography sx={{ fontWeight: 900, fontSize: '2rem', color: gradeMeta.color, lineHeight: 1 }}>
                                    {grade}
                                </Typography>
                                <Typography variant="caption" sx={{ color: gradeMeta.color, fontWeight: 700, fontSize: '0.58rem', letterSpacing: 0.5 }}>
                                    {gradeMeta.label.toUpperCase()}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Content Score</Typography>
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                    {/* Sentiment */}
                                    <Box sx={{
                                        display: 'flex', alignItems: 'center', gap: 0.75,
                                        px: 1.25, py: 0.4, borderRadius: '7px',
                                        bgcolor: alpha(sentimentColor, isDark ? 0.15 : 0.08),
                                        border: `1px solid ${alpha(sentimentColor, 0.25)}`,
                                    }}>
                                        {analysis.sentiment === 'positive'
                                            ? <SentimentPositiveIcon sx={{ fontSize: 14, color: sentimentColor }} />
                                            : analysis.sentiment === 'negative'
                                            ? <SentimentNegativeIcon sx={{ fontSize: 14, color: sentimentColor }} />
                                            : <SentimentNeutralIcon sx={{ fontSize: 14, color: sentimentColor }} />}
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: sentimentColor, fontSize: '0.7rem' }}>
                                            {analysis.sentiment?.charAt(0).toUpperCase() + analysis.sentiment?.slice(1)}
                                            {analysis.sentiment_score != null && ` · ${Math.round(analysis.sentiment_score * 100)}%`}
                                        </Typography>
                                    </Box>
                                    {/* Tone */}
                                    <Box sx={{
                                        px: 1.25, py: 0.4, borderRadius: '7px',
                                        bgcolor: alpha(toneMeta.color, isDark ? 0.15 : 0.08),
                                        border: `1px solid ${alpha(toneMeta.color, 0.25)}`,
                                    }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: toneMeta.color, fontSize: '0.7rem' }}>{tone}</Typography>
                                    </Box>
                                    {/* Virality */}
                                    <Box sx={{
                                        display: 'flex', alignItems: 'center', gap: 0.5,
                                        px: 1.25, py: 0.4, borderRadius: '7px',
                                        bgcolor: alpha(viralColor, isDark ? 0.15 : 0.08),
                                        border: `1px solid ${alpha(viralColor, 0.25)}`,
                                    }}>
                                        <BoltIcon sx={{ fontSize: 12, color: viralColor }} />
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: viralColor, fontSize: '0.7rem' }}>{virality} Virality</Typography>
                                    </Box>
                                    {/* CTA strength */}
                                    {ctaStrength && ctaStrength !== 'None' && (
                                        <Box sx={{
                                            display: 'flex', alignItems: 'center', gap: 0.5,
                                            px: 1.25, py: 0.4, borderRadius: '7px',
                                            bgcolor: alpha(ctaColor, isDark ? 0.15 : 0.08),
                                            border: `1px solid ${alpha(ctaColor, 0.25)}`,
                                        }}>
                                            <CTAIcon sx={{ fontSize: 12, color: ctaColor }} />
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: ctaColor, fontSize: '0.7rem' }}>{ctaStrength} CTA</Typography>
                                        </Box>
                                    )}
                                    {/* AI badge */}
                                    {analysis.ai_enriched && (
                                        <Box sx={{
                                            display: 'flex', alignItems: 'center', gap: 0.5,
                                            px: 1.25, py: 0.4, borderRadius: '7px',
                                            bgcolor: alpha('#ec4899', isDark ? 0.15 : 0.08),
                                            border: `1px solid ${alpha('#ec4899', 0.25)}`,
                                        }}>
                                            <AutoAwesomeIcon sx={{ fontSize: 12, color: '#ec4899' }} />
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#ec4899', fontSize: '0.7rem' }}>AI Enhanced</Typography>
                                        </Box>
                                    )}
                                    {/* Personalized badge */}
                                    {analysis.personalized && (
                                        <Box sx={{
                                            display: 'flex', alignItems: 'center', gap: 0.5,
                                            px: 1.25, py: 0.4, borderRadius: '7px',
                                            bgcolor: alpha('#8b5cf6', isDark ? 0.15 : 0.08),
                                            border: `1px solid ${alpha('#8b5cf6', 0.25)}`,
                                        }}>
                                            <PsychologyIcon sx={{ fontSize: 12, color: '#8b5cf6' }} />
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#8b5cf6', fontSize: '0.7rem' }}>Personalized</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                    {/* Score Breakdown bars */}
                    <Grid size={{ xs: 12, md: 5 }}>
                        <Typography variant="caption" sx={{
                            fontWeight: 700, color: 'text.disabled', fontSize: '0.65rem',
                            textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5, display: 'block',
                        }}>
                            Score Breakdown
                        </Typography>
                        <ScoreBreakdown
                            engagement={analysis.engagement_score || 0}
                            readability={(analysis.readability_score || 0) / 10}
                            sentiment={analysis.sentiment_score || 0.5}
                            sentimentLabel={analysis.sentiment}
                        />
                        {/* Benchmark comparison — shown when personalized */}
                        {analysis.vs_benchmark && (
                            <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${alpha('#8b5cf6', 0.15)}` }}>
                                <Typography variant="caption" sx={{
                                    fontWeight: 700, color: 'text.disabled', fontSize: '0.65rem',
                                    textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block',
                                }}>
                                    vs Your History
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Box sx={{
                                        flex: 1, p: 1, borderRadius: '8px', textAlign: 'center',
                                        bgcolor: alpha('#8b5cf6', isDark ? 0.1 : 0.06),
                                        border: `1px solid ${alpha('#8b5cf6', 0.2)}`,
                                    }}>
                                        <Typography sx={{ fontWeight: 900, fontSize: '1.05rem', color: '#8b5cf6', lineHeight: 1 }}>
                                            {analysis.vs_benchmark.your_avg}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', fontWeight: 600 }}>
                                            Your Avg
                                        </Typography>
                                    </Box>
                                    <Box sx={{
                                        flex: 1, p: 1, borderRadius: '8px', textAlign: 'center',
                                        bgcolor: alpha('#10b981', isDark ? 0.1 : 0.06),
                                        border: `1px solid ${alpha('#10b981', 0.2)}`,
                                    }}>
                                        <Typography sx={{ fontWeight: 900, fontSize: '1.05rem', color: '#10b981', lineHeight: 1 }}>
                                            {analysis.vs_benchmark.your_top_20}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', fontWeight: 600 }}>
                                            Top 20%
                                        </Typography>
                                    </Box>
                                </Box>
                                {analysis.optimal_time && (
                                    <Box sx={{
                                        mt: 1, px: 1.25, py: 0.6, borderRadius: '7px',
                                        bgcolor: alpha('#f59e0b', isDark ? 0.1 : 0.06),
                                        border: `1px solid ${alpha('#f59e0b', 0.2)}`,
                                    }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#d97706', fontSize: '0.7rem' }}>
                                            Best time to post: {analysis.optimal_time}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Paper>
            </Grid>

            {/* A1: Niche banner — shown when niche is known */}
            {analysis.niche && (
                <Grid size={12}>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1.25,
                        p: 1.5, borderRadius: '10px',
                        bgcolor: alpha('#8b5cf6', isDark ? 0.1 : 0.06),
                        border: `1px solid ${alpha('#8b5cf6', 0.25)}`,
                        borderLeft: '3px solid #8b5cf6',
                    }}>
                        <NicheIcon sx={{ fontSize: 16, color: '#8b5cf6', flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                            <Box component="span" sx={{ fontWeight: 700, color: '#8b5cf6' }}>Content DNA: </Box>
                            Scoring calibrated for your <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{analysis.niche}</Box> niche
                            {analysis.personalized ? ' — benchmarks from your real post history' : ''}
                        </Typography>
                    </Box>
                </Grid>
            )}

            {/* A2: No-profile nudge — shown when scoring is generic */}
            {!analysis.personalized && (
                <Grid size={12}>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        p: 1.5, borderRadius: '10px',
                        bgcolor: alpha('#8b5cf6', isDark ? 0.08 : 0.05),
                        border: `1px solid ${alpha('#8b5cf6', 0.2)}`,
                        borderLeft: '3px solid #8b5cf6',
                    }}>
                        <SyncIcon sx={{ fontSize: 18, color: '#8b5cf6', flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                            <Box component="span" sx={{ fontWeight: 700, color: '#8b5cf6' }}>Unlock personalized scoring — </Box>
                            sync your social posts to compare against your real engagement benchmarks and get niche-specific tips.
                        </Typography>
                    </Box>
                </Grid>
            )}

            {/* B0: Hook Strength — always shown when analysis has hook data */}
            {hookAnalysis && (
                <Grid size={12}>
                    <Section
                        title="Hook Strength"
                        icon={<BoltIcon sx={{ fontSize: 17 }} />}
                        color={hookColors[hookAnalysis.score] || '#6366f1'}
                        badge={
                            <Box sx={{
                                px: 1, py: 0.25, borderRadius: '6px',
                                bgcolor: alpha(hookColors[hookAnalysis.score] || '#6366f1', 0.12),
                                border: `1px solid ${alpha(hookColors[hookAnalysis.score] || '#6366f1', 0.3)}`,
                            }}>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: hookColors[hookAnalysis.score] || '#6366f1', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    {hookAnalysis.label || hookAnalysis.score}
                                </Typography>
                            </Box>
                        }
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.83rem' }}>
                                {hookAnalysis.reason}
                            </Typography>
                            {analysis.hook_rewrite && (
                                <Box sx={{
                                    p: 1.25, borderRadius: '8px',
                                    bgcolor: alpha(hookColors[hookAnalysis.score] || '#6366f1', isDark ? 0.1 : 0.06),
                                    border: `1px solid ${alpha(hookColors[hookAnalysis.score] || '#6366f1', 0.2)}`,
                                    borderLeft: `3px solid ${hookColors[hookAnalysis.score] || '#6366f1'}`,
                                }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: hookColors[hookAnalysis.score] || '#6366f1', fontSize: '0.7rem', display: 'block', mb: 0.4 }}>
                                        AI Rewrite Suggestion
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.primary', lineHeight: 1.6, fontSize: '0.83rem', fontStyle: 'italic' }}>
                                        "{analysis.hook_rewrite}"
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Section>
                </Grid>
            )}

            {/* B: Metric tiles — directly in outer Grid */}
            {analysis.engagement_score != null && (
                <Grid size={{ xs: 12, sm: 4 }}>
                    <MetricTile icon={<TrendingUpIcon sx={{ fontSize: 17 }} />} label="Engagement" value={`${Math.round(analysis.engagement_score * 100)}%`} sub="Predicted potential" color="#6366f1" />
                </Grid>
            )}
            {analysis.readability_score != null && (
                <Grid size={{ xs: 12, sm: 4 }}>
                    <MetricTile icon={<ReadingIcon sx={{ fontSize: 17 }} />} label="Readability" value={`${analysis.readability_score}/10`} sub={readabilityContext?.note || readingLevel || 'Flesch score'} color={readabilityContext?.match === 'good' ? '#10b981' : readabilityContext?.match === 'too_dense' ? '#f59e0b' : '#8b5cf6'} />
                </Grid>
            )}
            {analysis.word_count != null && (
                <Grid size={{ xs: 12, sm: 4 }}>
                    <MetricTile icon={<TextFieldsIcon sx={{ fontSize: 17 }} />} label="Word Count" value={analysis.word_count} sub={`${analysis.char_count ?? '—'} characters`} color="#06b6d4" />
                </Grid>
            )}

            {/* B1: Narrative + Niche Benchmark — side by side when both available */}
            {(narrative || nichePercentile) && (
                <>
                    {narrative && (
                        <Grid size={{ xs: 12, md: nichePercentile ? 7 : 12 }} sx={{ display: 'flex' }}>
                            <Box sx={{ flex: 1 }}>
                                <NarrativeCard narrative={narrative} card={{ ...cardSx, flex: 1, height: '100%' }} />
                            </Box>
                        </Grid>
                    )}
                    {nichePercentile && (
                        <Grid size={{ xs: 12, md: narrative ? 5 : 12 }} sx={{ display: 'flex' }}>
                            <Box sx={{ flex: 1 }}>
                                <NichePercentileCard nichePercentile={nichePercentile} nicheFit={nicheFit} card={{ ...cardSx, flex: 1, height: '100%' }} />
                            </Box>
                        </Grid>
                    )}
                </>
            )}

            {/* B2: Voice Consistency */}
            {voiceConsistency && (
                <Grid size={12}>
                    <VoiceConsistencyCard voiceConsistency={voiceConsistency} card={cardSx} />
                </Grid>
            )}

            {/* B3: Retention + Engagement Breakdown — side by side */}
            {(retention?.available || engagementBreakdown) && (
                <>
                    {retention?.available && (
                        <Grid size={{ xs: 12, md: engagementBreakdown ? 7 : 12 }} sx={{ display: 'flex' }}>
                            <Box sx={{ flex: 1 }}>
                                <RetentionCard retention={retention} card={{ ...cardSx, flex: 1, height: '100%' }} />
                            </Box>
                        </Grid>
                    )}
                    {engagementBreakdown && (
                        <Grid size={{ xs: 12, md: retention?.available ? 5 : 12 }} sx={{ display: 'flex' }}>
                            <Box sx={{ flex: 1 }}>
                                <EngagementBreakdownCard engagementBreakdown={engagementBreakdown} card={{ ...cardSx, flex: 1, height: '100%' }} />
                            </Box>
                        </Grid>
                    )}
                </>
            )}

            {/* B4: Discoverability — full width (checklist needs space) */}
            {discoverability?.available && (
                <Grid size={12}>
                    <DiscoverabilityCard discoverability={discoverability} card={cardSx} />
                </Grid>
            )}

            {/* C: Platform Fitness */}
            {hasPlatformFitness && (
                <Grid size={{ xs: 12, md: hasSignals ? 7 : 12 }} sx={{ display: 'flex' }}>
                    <Section title="Platform Fitness" icon={<PsychologyIcon sx={{ fontSize: 17 }} />} color="#6366f1" sx={{ flex: 1 }}
                        badge={bestPlatform && (
                            <Box sx={{
                                px: 1, py: 0.25, borderRadius: '6px',
                                bgcolor: alpha(_PLATFORM_COLORS[bestPlatform] || '#6366f1', 0.1),
                                border: `1px solid ${alpha(_PLATFORM_COLORS[bestPlatform] || '#6366f1', 0.25)}`,
                            }}>
                                <Typography variant="caption" sx={{
                                    fontWeight: 700, fontSize: '0.68rem',
                                    color: _PLATFORM_COLORS[bestPlatform] || '#6366f1',
                                }}>
                                    Detected: {bestPlatform}
                                </Typography>
                            </Box>
                        )}
                    >
                        <Grid container spacing={5} alignItems="center">
                            {/* Radar chart — always shows all 5 platforms */}
                            <Grid size={{ xs: 12, sm: 5 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                                <PlatformRadar scores={platformScores} />
                            </Grid>
                            {/* Progress bars — sorted: detected platform first, then by score */}
                            <Grid size={{ xs: 12, sm: 7 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {sortedPlatformEntries.map(([plat, score]) => {
                                        const meta = PLATFORM_META[plat] || { color: '#6366f1', limit: '' };
                                        const barColor = score >= 70 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';
                                        const isPrimary = plat === bestPlatform;
                                        return (
                                            <Box key={plat}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75, gap: 1 }}>
                                                    <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: meta.color, flexShrink: 0 }} />
                                                    <Typography sx={{ fontWeight: isPrimary ? 800 : 700, flex: 1, fontSize: '0.85rem' }}>{plat}</Typography>
                                                    {isPrimary && (
                                                        <Box sx={{
                                                            px: 0.75, py: 0.15, borderRadius: '4px',
                                                            bgcolor: alpha(meta.color, 0.12),
                                                            border: `1px solid ${alpha(meta.color, 0.3)}`,
                                                        }}>
                                                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: meta.color, lineHeight: 1 }}>PRIMARY</Typography>
                                                        </Box>
                                                    )}
                                                    <Typography sx={{ color: 'text.disabled', mr: 1, fontSize: '0.78rem' }}>{meta.limit}</Typography>
                                                    <Typography sx={{ fontWeight: 800, color: barColor, minWidth: 36, textAlign: 'right', fontSize: '0.88rem' }}>{score}%</Typography>
                                                </Box>
                                                <LinearProgress variant="determinate" value={score} sx={{
                                                    height: isPrimary ? 9 : 7, borderRadius: '4px',
                                                    bgcolor: alpha(barColor, isDark ? 0.15 : 0.1),
                                                    '& .MuiLinearProgress-bar': { borderRadius: '4px', bgcolor: barColor },
                                                }} />
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Grid>
                        </Grid>
                    </Section>
                </Grid>
            )}

            {/* D: Content Signals */}
            {hasSignals && (
                <Grid size={{ xs: 12, md: hasPlatformFitness ? 5 : 12 }} sx={{ display: 'flex' }}>
                    <Section title="Content Signals" icon={<CheckIcon sx={{ fontSize: 17 }} />} color="#10b981" sx={{ flex: 1 }}>
                        <Grid container spacing={1.25}>
                            {signalDefs.map(({ key, label, tip }) => {
                                const present = signals[key];
                                return (
                                    <Grid size={{ xs: 12, sm: 6 }} key={key}>
                                        <Box sx={{
                                            display: 'flex', alignItems: 'flex-start', gap: 1.25,
                                            p: 1.5, borderRadius: '8px',
                                            bgcolor: present ? alpha('#10b981', isDark ? 0.1 : 0.05) : alpha('#ef4444', isDark ? 0.08 : 0.04),
                                            border: `1px solid ${alpha(present ? '#10b981' : '#ef4444', isDark ? 0.2 : 0.15)}`,
                                        }}>
                                            {present
                                                ? <CheckIcon sx={{ fontSize: 15, color: '#10b981', mt: 0.15, flexShrink: 0 }} />
                                                : <XIcon sx={{ fontSize: 15, color: '#ef4444', mt: 0.15, flexShrink: 0 }} />}
                                            <Box>
                                                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: 'text.primary', lineHeight: 1.3 }}>{label}</Typography>
                                                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.67rem' }}>{tip}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Section>
                </Grid>
            )}

            {/* E: Keywords */}
            {hasKeywords && (
                <Grid size={{ xs: 12, sm: hasEntities ? 7 : 12 }} sx={{ display: 'flex' }}>
                    <Section title="Keywords" icon={<TagIcon sx={{ fontSize: 17 }} />} color="#06b6d4" sx={{ flex: 1 }}
                        badge={
                            <Box sx={{ px: 1, py: 0.25, borderRadius: '6px', bgcolor: alpha('#06b6d4', 0.1), border: `1px solid ${alpha('#06b6d4', 0.2)}` }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#06b6d4', fontSize: '0.68rem' }}>{analysis.keywords.length}</Typography>
                            </Box>
                        }
                    >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {analysis.keywords.map((kw, i) => (
                                <Box key={i} sx={{
                                    px: 1.25, py: 0.5, borderRadius: '6px',
                                    bgcolor: alpha('#06b6d4', isDark ? 0.1 : 0.07),
                                    border: `1px solid ${alpha('#06b6d4', 0.2)}`,
                                }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#06b6d4', fontSize: '0.78rem' }}>{kw}</Typography>
                                </Box>
                            ))}
                        </Box>
                        {hasMissingKeywords && (
                            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px dashed ${alpha('#f59e0b', 0.3)}` }}>
                                <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 700, mb: 0.75, display: 'block' }}>
                                    Brand keywords not in this draft:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 0.75 }}>
                                    {analysis.missing_keywords.map((kw, i) => (
                                        <Box key={i} sx={{
                                            px: 1.25, py: 0.5, borderRadius: '6px',
                                            border: `1px solid ${alpha('#f59e0b', 0.4)}`,
                                            bgcolor: alpha('#f59e0b', isDark ? 0.1 : 0.08),
                                        }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#ca8a04', fontSize: '0.78rem' }}>{kw.word || kw}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                                    Weave these in to reinforce your niche
                                </Typography>
                            </Box>
                        )}
                    </Section>
                </Grid>
            )}

            {/* E: Named Entities */}
            {hasEntities && (
                <Grid size={{ xs: 12, sm: hasKeywords ? 5 : 12 }} sx={{ display: 'flex' }}>
                    <Section title="Named Entities" icon={<AutoAwesomeIcon sx={{ fontSize: 17 }} />} color="#f59e0b" sx={{ flex: 1 }}
                        badge={
                            <Box sx={{ px: 1, py: 0.25, borderRadius: '6px', bgcolor: alpha('#f59e0b', 0.1), border: `1px solid ${alpha('#f59e0b', 0.2)}` }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#d97706', fontSize: '0.68rem' }}>{analysis.entities.length}</Typography>
                            </Box>
                        }
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {analysis.entities.map(([ent, type], i) => (
                                <Box key={i} sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    p: 1, borderRadius: '8px',
                                    bgcolor: alpha('#f59e0b', isDark ? 0.08 : 0.04),
                                }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{ent}</Typography>
                                    <Box sx={{ px: 1, py: 0.2, borderRadius: '5px', bgcolor: alpha('#f59e0b', 0.12) }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#d97706', fontSize: '0.65rem' }}>{type}</Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Section>
                </Grid>
            )}

            {/* F: Hashtag Suggestions */}
            {hasHashtags && (
                <Grid size={{ xs: 12, md: hasSuggestions ? 5 : 12 }} sx={{ display: 'flex' }}>
                    <Section
                        title="Suggested Hashtags"
                        icon={<HashtagIcon sx={{ fontSize: 17 }} />}
                        color="#0ea5e9"
                        sx={{ flex: 1 }}
                        badge={
                            <Box sx={{ px: 1, py: 0.25, borderRadius: '6px', bgcolor: alpha('#0ea5e9', 0.1), border: `1px solid ${alpha('#0ea5e9', 0.2)}` }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#0ea5e9', fontSize: '0.68rem' }}>{hashtagSuggestions.length} tags</Typography>
                            </Box>
                        }
                    >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {hashtagSuggestions.map((tag, i) => (
                                <Box key={i} sx={{
                                    px: 1.5, py: 0.6, borderRadius: '6px',
                                    bgcolor: alpha('#0ea5e9', isDark ? 0.12 : 0.07),
                                    border: `1px solid ${alpha('#0ea5e9', 0.25)}`,
                                    borderLeft: '3px solid #0ea5e9',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                    '&:hover': { bgcolor: alpha('#0ea5e9', isDark ? 0.2 : 0.12), transform: 'translateY(-1px)' },
                                }}
                                    onClick={() => navigator.clipboard?.writeText(tag)}
                                >
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#0ea5e9', fontSize: '0.8rem' }}>{tag}</Typography>
                                </Box>
                            ))}
                        </Box>
                        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 1.5, fontSize: '0.7rem' }}>
                            Click any hashtag to copy it
                        </Typography>
                    </Section>
                </Grid>
            )}

            {/* G: AI Suggestions */}
            {/* G0: Voice-Aligned Rewrite — shown when AI returns one */}
            {analysis.voice_aligned_rewrite && (
                <Grid size={12}>
                    <Section
                        title="Voice-Aligned Rewrite"
                        icon={<VoiceIcon sx={{ fontSize: 17 }} />}
                        color="#6366f1"
                        badge={<Chip label="AI" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, borderRadius: '6px', bgcolor: alpha('#6366f1', 0.1), color: '#6366f1' }} />}
                    >
                        <Box sx={{
                            p: 2, borderRadius: '10px',
                            bgcolor: alpha('#6366f1', isDark ? 0.08 : 0.04),
                            border: `1px solid ${alpha('#6366f1', 0.2)}`,
                            borderLeft: '3px solid #6366f1',
                        }}>
                            <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.7, fontSize: '0.88rem', whiteSpace: 'pre-wrap' }}>
                                {analysis.voice_aligned_rewrite}
                            </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 1, fontSize: '0.7rem' }}>
                            Calibrated to your {analysis.niche ? `${analysis.niche} ` : ''}brand voice and platform style
                        </Typography>
                    </Section>
                </Grid>
            )}

            {hasSuggestions && (
                <Grid size={{ xs: 12, md: hasHashtags ? 7 : 12 }} sx={{ display: 'flex' }}>
                    <Section
                        title={analysis.ai_enriched ? 'AI Suggestions' : 'Improvement Tips'}
                        icon={<AutoAwesomeIcon sx={{ fontSize: 17 }} />}
                        color="#ec4899"
                        sx={{ flex: 1 }}
                        badge={analysis.ai_enriched && (
                            <Chip label="AI powered" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, borderRadius: '6px', bgcolor: alpha('#ec4899', 0.1), color: '#ec4899' }} />
                        )}
                    >
                        {(analysis.tone_note || analysis.readability_note) && (
                            <Box sx={{ mb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {analysis.tone_note && (
                                    <Box sx={{
                                        display: 'flex', alignItems: 'flex-start', gap: 1,
                                        p: 1.25, borderRadius: '8px',
                                        bgcolor: alpha('#6366f1', isDark ? 0.1 : 0.07),
                                        border: `1px solid ${alpha('#6366f1', 0.15)}`,
                                    }}>
                                        <PsychologyIcon sx={{ fontSize: 16, color: '#6366f1', flexShrink: 0, mt: 0.2 }} />
                                        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5, fontSize: '0.82rem' }}>
                                            <Box component="span" sx={{ fontWeight: 700, color: '#6366f1' }}>Tone: </Box>
                                            {analysis.tone_note}
                                        </Typography>
                                    </Box>
                                )}
                                {analysis.readability_note && (
                                    <Box sx={{
                                        display: 'flex', alignItems: 'flex-start', gap: 1,
                                        p: 1.25, borderRadius: '8px',
                                        bgcolor: alpha('#0ea5e9', isDark ? 0.1 : 0.07),
                                        border: `1px solid ${alpha('#0ea5e9', 0.15)}`,
                                    }}>
                                        <ReadingIcon sx={{ fontSize: 16, color: '#0ea5e9', flexShrink: 0, mt: 0.2 }} />
                                        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5, fontSize: '0.82rem' }}>
                                            <Box component="span" sx={{ fontWeight: 700, color: '#0ea5e9' }}>Readability: </Box>
                                            {analysis.readability_note}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                        <Grid container spacing={1.5}>
                            {analysis.suggestions.map((s, i) => (
                                <Grid size={{ xs: 12, sm: 6 }} key={i}>
                                    <Box sx={{
                                        display: 'flex', alignItems: 'flex-start', gap: 1.5,
                                        p: 1.75, borderRadius: '8px', height: '100%',
                                        bgcolor: isDark ? alpha('#10b981', 0.06) : alpha('#10b981', 0.04),
                                        border: `1px solid ${alpha('#10b981', isDark ? 0.15 : 0.12)}`,
                                        borderLeft: '3px solid #10b981',
                                    }}>
                                        <Box sx={{
                                            width: 22, height: 22, borderRadius: '6px', flexShrink: 0,
                                            bgcolor: alpha('#10b981', 0.15),
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: '#10b981', fontSize: '0.72rem', fontWeight: 800,
                                        }}>
                                            {i + 1}
                                        </Box>
                                        <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6, fontSize: '0.85rem' }}>
                                            {s}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Section>
                </Grid>
            )}
        </Grid>
    );
}

// ── Orchestrator ──────────────────────────────────────────────────────────────
export default function ResultsPanel({ analysis, loading }) {
    const hasResults = !!(analysis && Object.keys(analysis).length > 0 && !analysis.error);
    if (loading) return <LoadingState />;
    if (!hasResults) return <EmptyState />;
    return <AnalysisResults analysis={analysis} />;
}
